<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="show"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5 overflow-y-auto"
        @keydown.esc="handleClose"
      >
        <!-- Backdrop Blur Overlay -->
        <div
          class="fixed inset-0 bg-slate-950/70 dark:bg-black/80 backdrop-blur-md transition-opacity"
          @click="handleClose"
        />

        <!-- Modal Dialog Box -->
        <div
          class="relative w-full max-w-4xl bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-[#263449] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] z-10 animate-in fade-in zoom-in-95 duration-200"
          role="dialog"
          aria-modal="true"
        >
          <!-- Top Header Accent Gradient -->
          <div class="h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500"></div>

          <!-- Header -->
          <div
            class="p-4 sm:p-6 border-b border-slate-100 dark:border-[#263449] flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50 dark:bg-[#18243A]/30"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 to-blue-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/25 shrink-0"
              >
                <PieChart class="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">
                    Student Status & Ratio Analytics
                  </h3>
                  <span
                    class="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Live Data
                  </span>
                </div>
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Visual distribution, active retention rates, and class-wise enrollment analytics
                </p>
              </div>
            </div>

            <!-- View Switcher & Close Button -->
            <div class="flex items-center gap-2 self-end sm:self-auto">
              <!-- Tab Switcher -->
              <div
                class="flex items-center p-1 bg-white dark:bg-[#18243A] rounded-xl border border-slate-200 dark:border-[#263449] shadow-2xs text-xs font-bold"
              >
                <button
                  type="button"
                  @click="activeChartType = 'donut'"
                  :class="[
                    'px-3 py-1.5 rounded-lg transition-all duration-150 flex items-center gap-1.5 cursor-pointer',
                    activeChartType === 'donut'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
                  ]"
                >
                  <PieChart class="w-3.5 h-3.5" />
                  <span>Ratio Donut</span>
                </button>
                <button
                  type="button"
                  @click="activeChartType = 'bar'"
                  :class="[
                    'px-3 py-1.5 rounded-lg transition-all duration-150 flex items-center gap-1.5 cursor-pointer',
                    activeChartType === 'bar'
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
                  ]"
                >
                  <BarChart3 class="w-3.5 h-3.5" />
                  <span>Class Breakdown</span>
                </button>
              </div>

              <!-- Close Button -->
              <button
                type="button"
                @click="handleClose"
                class="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#18243A] transition cursor-pointer"
                title="Close modal"
              >
                <X class="w-5 h-5" />
              </button>
            </div>
          </div>

          <!-- Body Container (Scrollable) -->
          <div class="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 custom-scrollbar">
            <!-- 1. KPI Stats Summary Cards Row -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <!-- Total Students -->
              <div
                class="bg-slate-50/80 dark:bg-[#18243A]/50 rounded-2xl p-3.5 border border-slate-200/80 dark:border-[#263449] flex flex-col justify-between"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-400">Total</span>
                  <div class="p-1.5 bg-blue-50 dark:bg-blue-950/60 rounded-lg text-blue-600 dark:text-blue-400">
                    <Users class="w-4 h-4" />
                  </div>
                </div>
                <div>
                  <p class="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                    {{ displayTotalStudents }}
                  </p>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Enrolled Students</p>
                </div>
              </div>

              <!-- Active Students -->
              <div
                class="bg-emerald-50/40 dark:bg-emerald-950/20 rounded-2xl p-3.5 border border-emerald-200/80 dark:border-emerald-800/40 flex flex-col justify-between"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Active</span>
                  <div class="p-1.5 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg text-emerald-600 dark:text-emerald-400">
                    <UserCheck class="w-4 h-4" />
                  </div>
                </div>
                <div>
                  <div class="flex items-baseline justify-between">
                    <p class="text-2xl font-black text-emerald-800 dark:text-emerald-300 tracking-tight">
                      {{ activeStudents }}
                    </p>
                    <span class="text-xs font-black text-emerald-600 dark:text-emerald-400">
                      {{ activePct }}%
                    </span>
                  </div>
                  <p class="text-[11px] text-emerald-600 dark:text-emerald-400/80 mt-0.5">Regularly Attending</p>
                </div>
              </div>

              <!-- Inactive / Leaved -->
              <div
                class="bg-rose-50/40 dark:bg-rose-950/20 rounded-2xl p-3.5 border border-rose-200/80 dark:border-rose-800/40 flex flex-col justify-between"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400">Inactive</span>
                  <div class="p-1.5 bg-rose-100 dark:bg-rose-900/50 rounded-lg text-rose-600 dark:text-rose-400">
                    <UserX class="w-4 h-4" />
                  </div>
                </div>
                <div>
                  <div class="flex items-baseline justify-between">
                    <p class="text-2xl font-black text-rose-800 dark:text-rose-300 tracking-tight">
                      {{ inactiveStudents }}
                    </p>
                    <span class="text-xs font-black text-rose-600 dark:text-rose-400">
                      {{ inactivePct }}%
                    </span>
                  </div>
                  <p class="text-[11px] text-rose-600 dark:text-rose-400/80 mt-0.5">Leaved / Inactive</p>
                </div>
              </div>

              <!-- Alumni / Passout -->
              <div
                class="bg-indigo-50/40 dark:bg-indigo-950/20 rounded-2xl p-3.5 border border-indigo-200/80 dark:border-indigo-800/40 flex flex-col justify-between"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <span class="text-[11px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400">Alumni</span>
                  <div class="p-1.5 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg text-indigo-600 dark:text-indigo-400">
                    <GraduationCap class="w-4 h-4" />
                  </div>
                </div>
                <div>
                  <div class="flex items-baseline justify-between">
                    <p class="text-2xl font-black text-indigo-800 dark:text-indigo-300 tracking-tight">
                      {{ graduatedStudents }}
                    </p>
                    <span class="text-xs font-black text-indigo-600 dark:text-indigo-400">
                      {{ graduatedPct }}%
                    </span>
                  </div>
                  <p class="text-[11px] text-indigo-600 dark:text-indigo-400/80 mt-0.5">Passed Out Alumni</p>
                </div>
              </div>
            </div>

            <!-- 2. Chart Section Viewports -->
            <!-- VIEW A: RATIO DONUT & DEMOGRAPHIC BREAKDOWN -->
            <div
              v-show="activeChartType === 'donut'"
              class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center bg-slate-50/60 dark:bg-[#18243A]/30 p-4 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449]"
            >
              <!-- Left: Canvas Donut Chart -->
              <div class="lg:col-span-6 flex flex-col items-center justify-center relative min-h-[260px]">
                <div class="relative w-64 h-64 sm:w-72 sm:h-72">
                  <canvas ref="donutCanvasRef"></canvas>
                </div>
              </div>

              <!-- Right: Detailed Metric Cards -->
              <div class="lg:col-span-6 space-y-3.5">
                <div class="border-b border-slate-200/80 dark:border-[#263449] pb-3">
                  <h4 class="text-sm font-bold text-slate-900 dark:text-white">
                    Directory Distribution Breakdown
                  </h4>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Live proportional share of the student database
                  </p>
                </div>

                <!-- Active Card -->
                <div
                  class="p-3 rounded-xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] shadow-xs flex items-center justify-between"
                >
                  <div class="flex items-center gap-3">
                    <span class="w-3.5 h-3.5 rounded-full bg-emerald-500 shadow-xs"></span>
                    <div>
                      <p class="text-xs font-bold text-slate-800 dark:text-slate-100">Active Directory</p>
                      <p class="text-[11px] text-slate-400">Attending classes regularly</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-black text-slate-900 dark:text-white">{{ activeStudents }} students</p>
                    <p class="text-xs font-bold text-emerald-600 dark:text-emerald-400">{{ activePct }}% Share</p>
                  </div>
                </div>

                <!-- Inactive Card -->
                <div
                  class="p-3 rounded-xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] shadow-xs flex items-center justify-between"
                >
                  <div class="flex items-center gap-3">
                    <span class="w-3.5 h-3.5 rounded-full bg-rose-500 shadow-xs"></span>
                    <div>
                      <p class="text-xs font-bold text-slate-800 dark:text-slate-100">Inactive / Leaved</p>
                      <p class="text-[11px] text-slate-400">Withdrawal or deactivated</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-black text-slate-900 dark:text-white">{{ inactiveStudents }} students</p>
                    <p class="text-xs font-bold text-rose-600 dark:text-rose-400">{{ inactivePct }}% Share</p>
                  </div>
                </div>

                <!-- Passout Card -->
                <div
                  v-if="graduatedStudents > 0"
                  class="p-3 rounded-xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] shadow-xs flex items-center justify-between"
                >
                  <div class="flex items-center gap-3">
                    <span class="w-3.5 h-3.5 rounded-full bg-indigo-500 shadow-xs"></span>
                    <div>
                      <p class="text-xs font-bold text-slate-800 dark:text-slate-100">Graduated Alumni</p>
                      <p class="text-[11px] text-slate-400">Successfully passed out</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-black text-slate-900 dark:text-white">{{ graduatedStudents }} students</p>
                    <p class="text-xs font-bold text-indigo-600 dark:text-indigo-400">{{ graduatedPct }}% Share</p>
                  </div>
                </div>

                <!-- Health Ratio Progress Bar -->
                <div class="pt-2">
                  <div class="flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5">
                    <span>Overall Retention Score</span>
                    <span class="text-emerald-600 dark:text-emerald-400 font-extrabold">{{ activePct }}% Optimal</span>
                  </div>
                  <div class="w-full h-3 bg-slate-200 dark:bg-[#1E293B] rounded-full overflow-hidden flex shadow-inner">
                    <div
                      class="bg-emerald-500 h-full transition-all duration-700"
                      :style="{ width: `${activePct}%` }"
                      title="Active Students"
                    ></div>
                    <div
                      class="bg-rose-500 h-full transition-all duration-700"
                      :style="{ width: `${inactivePct}%` }"
                      title="Inactive Students"
                    ></div>
                    <div
                      v-if="graduatedPct > 0"
                      class="bg-indigo-500 h-full transition-all duration-700"
                      :style="{ width: `${graduatedPct}%` }"
                      title="Passout Students"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- VIEW B: CLASS-WISE STATUS BREAKDOWN (BAR CHART) -->
            <div
              v-show="activeChartType === 'bar'"
              class="space-y-4 bg-slate-50/60 dark:bg-[#18243A]/30 p-4 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449]"
            >
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 dark:border-[#263449] pb-3">
                <div>
                  <h4 class="text-sm font-bold text-slate-900 dark:text-white">
                    Class-by-Class Student Distribution
                  </h4>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    Active vs Inactive headcounts across classes and sections
                  </p>
                </div>
                <div class="flex items-center gap-3 text-xs font-bold">
                  <span class="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Active
                  </span>
                  <span class="inline-flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
                    <span class="w-2.5 h-2.5 rounded-full bg-rose-500"></span> Inactive
                  </span>
                </div>
              </div>

              <!-- Bar Chart Canvas -->
              <div class="relative h-64 sm:h-80 w-full">
                <canvas ref="barCanvasRef"></canvas>
              </div>

              <!-- Class Quick Stats Mini Table -->
              <div v-if="effectiveClassBreakdown.length > 0" class="overflow-x-auto pt-2 max-h-64 overflow-y-auto custom-scrollbar border border-slate-200/80 dark:border-[#263449] rounded-xl bg-white/70 dark:bg-[#111C30]/50 shadow-2xs">
                <table class="w-full text-left text-xs border-collapse">
                  <thead class="sticky top-0 bg-slate-100 dark:bg-[#18243A] z-1 shadow-2xs">
                    <tr class="border-b border-slate-200 dark:border-[#263449] text-slate-500 dark:text-slate-400 uppercase text-[10px] font-bold">
                      <th class="py-2.5 px-3">Class</th>
                      <th class="py-2.5 px-3 text-right">Active</th>
                      <th class="py-2.5 px-3 text-right">Inactive</th>
                      <th class="py-2.5 px-3 text-right">Total</th>
                      <th class="py-2.5 px-3 text-right">Active Rate</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-[#263449]/70">
                    <tr
                      v-for="item in effectiveClassBreakdown"
                      :key="item.name"
                      class="hover:bg-white/80 dark:hover:bg-[#18243A]/60 transition-colors"
                    >
                      <td class="py-2.5 px-3 font-bold text-slate-800 dark:text-slate-200">
                        {{ item.name }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-bold text-emerald-600 dark:text-emerald-400">
                        {{ item.active }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-bold text-rose-600 dark:text-rose-400">
                        {{ item.inactive }}
                      </td>
                      <td class="py-2.5 px-3 text-right font-black text-slate-900 dark:text-white">
                        {{ item.total }}
                      </td>
                      <td class="py-2.5 px-3 text-right">
                        <span
                          class="px-2 py-0.5 rounded-md font-bold text-[11px]"
                          :class="
                            item.total > 0 && Math.round((item.active / item.total) * 100) >= 80
                              ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400'
                              : 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400'
                          "
                        >
                          {{ item.total > 0 ? Math.round((item.active / item.total) * 100) : 0 }}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="p-4 border-t border-slate-100 dark:border-[#263449] bg-slate-50/50 dark:bg-[#18243A]/30 flex items-center justify-between"
          >
            <p class="text-xs text-slate-400 dark:text-slate-500">
              Generated in real-time from active student directory records
            </p>
            <button
              type="button"
              @click="handleClose"
              class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 active:scale-95 shadow-md shadow-indigo-600/25 transition cursor-pointer"
            >
              Done & Close
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
  import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
  import { Chart, registerables } from "chart.js/auto";
  import {
    PieChart,
    BarChart3,
    Users,
    UserCheck,
    UserX,
    GraduationCap,
    X,
  } from "lucide-vue-next";

  Chart.register(...registerables);

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    students: {
      type: Array,
      default: () => [],
    },
    totalStudents: {
      type: Number,
      default: 0,
    },
    activeStudents: {
      type: Number,
      default: 0,
    },
    graduatedStudents: {
      type: Number,
      default: 0,
    },
    inactiveStudents: {
      type: Number,
      default: 0,
    },
    classBreakdown: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(["close"]);

  const activeChartType = ref("donut"); // 'donut' | 'bar'
  const donutCanvasRef = ref(null);
  const barCanvasRef = ref(null);

  let donutChartInstance = null;
  let barChartInstance = null;

  /* =========================================
     COMPUTED PERCENTAGES & METRICS
  ========================================= */
  const displayTotalStudents = computed(() => {
    if (typeof props.totalStudents === "number" && props.totalStudents > 0) {
      return props.totalStudents;
    }
    return (props.activeStudents || 0) + (props.inactiveStudents || 0) || props.students.length;
  });

  const totalStatus = computed(() => {
    return (props.activeStudents || 0) + (props.inactiveStudents || 0);
  });

  const activePct = computed(() => {
    const total = totalStatus.value || displayTotalStudents.value;
    return total > 0 ? Math.round((props.activeStudents / total) * 100) : 0;
  });

  const inactivePct = computed(() => {
    const total = totalStatus.value || displayTotalStudents.value;
    return total > 0 ? Math.round((props.inactiveStudents / total) * 100) : 0;
  });

  const graduatedPct = computed(() => {
    const total = displayTotalStudents.value || props.graduatedStudents;
    return total > 0 ? Math.round((props.graduatedStudents / total) * 100) : 0;
  });

  /* =========================================
     CLASS-WISE BREAKDOWN
  ========================================= */
  const effectiveClassBreakdown = computed(() => {
    if (props.classBreakdown && props.classBreakdown.length > 0) {
      return props.classBreakdown;
    }
    const map = {};
    (props.students || []).forEach((s) => {
      let className = "Unassigned";
      if (typeof s.class === "object" && s.class) {
        className = s.class.name || "Unassigned";
        if (s.class.section) {
          className += ` - ${s.class.section.replace(/section\s*/i, "")}`;
        }
      } else if (typeof s.class === "string" && s.class.trim()) {
        className = s.class.trim();
      }

      if (!map[className]) {
        map[className] = {
          name: className,
          active: 0,
          inactive: 0,
          passout: 0,
          total: 0,
        };
      }

      const status = String(s.status || "active").toLowerCase();
      if (status === "active") {
        map[className].active++;
      } else if (status === "leaved" || status === "inactive") {
        map[className].inactive++;
      } else if (status === "passout") {
        map[className].passout++;
      }
      map[className].total++;
    });

    return Object.values(map).sort((a, b) => b.total - a.total);
  });

  /* =========================================
     CHART INITIALIZATION & RENDERING
  ========================================= */
  const renderDonutChart = () => {
    if (!donutCanvasRef.value) return;

    if (donutChartInstance) {
      donutChartInstance.destroy();
      donutChartInstance = null;
    }

    const labels = ["Active", "Inactive"];
    const data = [props.activeStudents, props.inactiveStudents];
    const colors = ["#10b981", "#f43f5e"];
    const hoverColors = ["#059669", "#e11d48"];

    if (props.graduatedStudents > 0) {
      labels.push("Passout");
      data.push(props.graduatedStudents);
      colors.push("#6366f1");
      hoverColors.push("#4f46e5");
    }

    const isDark = document.documentElement.classList.contains("dark");

    donutChartInstance = new Chart(donutCanvasRef.value, {
      type: "doughnut",
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: colors,
            hoverBackgroundColor: hoverColors,
            borderColor: isDark ? "#111C30" : "#ffffff",
            borderWidth: 3,
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "74%",
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 12,
              padding: 16,
              color: isDark ? "#A6B4C8" : "#475569",
              font: {
                size: 12,
                weight: "bold",
              },
            },
          },
          tooltip: {
            backgroundColor: isDark ? "#0F172A" : "#1E293B",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            padding: 12,
            cornerRadius: 12,
            displayColors: true,
            callbacks: {
              label: (context) => {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const val = context.raw || 0;
                const pct = total > 0 ? Math.round((val / total) * 100) : 0;
                return ` ${context.label}: ${val} students (${pct}%)`;
              },
            },
          },
        },
      },
    });
  };

  const renderBarChart = () => {
    if (!barCanvasRef.value) return;

    if (barChartInstance) {
      barChartInstance.destroy();
      barChartInstance = null;
    }

    const classesToShow = effectiveClassBreakdown.value;
    const labels = classesToShow.map((c) => c.name);
    const activeData = classesToShow.map((c) => c.active);
    const inactiveData = classesToShow.map((c) => c.inactive);

    const isDark = document.documentElement.classList.contains("dark");

    // Plugin to display numbers clearly right on top of every bar!
    const barValueLabelsPlugin = {
      id: "barValueLabels",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const textColor = isDark ? "#E2E8F0" : "#0F172A";

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (meta.hidden) return;

          meta.data.forEach((bar, index) => {
            const val = dataset.data[index];
            if (val === undefined || val === null || val === 0) return;

            ctx.save();
            ctx.font = "bold 11px system-ui, -apple-system, sans-serif";
            ctx.fillStyle = textColor;
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillText(String(val), bar.x, bar.y - 4);
            ctx.restore();
          });
        });
      },
    };

    barChartInstance = new Chart(barCanvasRef.value, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Active Students",
            data: activeData,
            backgroundColor: "#10b981",
            borderRadius: 6,
            maxBarThickness: 34,
          },
          {
            label: "Inactive Students",
            data: inactiveData,
            backgroundColor: "#f43f5e",
            borderRadius: 6,
            maxBarThickness: 34,
          },
        ],
      },
      plugins: [barValueLabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: isDark ? "#0F172A" : "#1E293B",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            padding: 12,
            cornerRadius: 12,
            callbacks: {
              afterBody: (items) => {
                if (!items || !items.length) return "";
                const dataIndex = items[0].dataIndex;
                const cls = classesToShow[dataIndex];
                if (!cls) return "";
                const rate =
                  cls.total > 0 ? Math.round((cls.active / cls.total) * 100) : 0;
                return `Total Enrolled: ${cls.total} (Active Rate: ${rate}%)`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: isDark ? "#A6B4C8" : "#64748B",
              font: {
                size: 11,
                weight: "bold",
              },
              maxRotation: 45,
              minRotation: 0,
            },
          },
          y: {
            beginAtZero: true,
            grace: "20%",
            grid: {
              color: isDark ? "#263449" : "#E2E8F0",
            },
            ticks: {
              precision: 0,
              stepSize: 1,
              color: isDark ? "#A6B4C8" : "#64748B",
              font: {
                size: 11,
              },
            },
          },
        },
      },
    });
  };

  const updateCharts = () => {
    nextTick(() => {
      if (activeChartType.value === "donut") {
        renderDonutChart();
      } else {
        renderBarChart();
      }
    });
  };

  watch(
    () => props.show,
    (isOpen) => {
      if (isOpen) {
        updateCharts();
      } else {
        if (donutChartInstance) {
          donutChartInstance.destroy();
          donutChartInstance = null;
        }
        if (barChartInstance) {
          barChartInstance.destroy();
          barChartInstance = null;
        }
      }
    },
  );

  watch(activeChartType, () => {
    updateCharts();
  });

  watch(
    () => [
      props.students,
      props.classBreakdown,
      props.totalStudents,
      props.activeStudents,
      props.inactiveStudents,
    ],
    () => {
      if (props.show) {
        updateCharts();
      }
    },
    { deep: true },
  );

  /* =========================================
     LIFECYCLE & KEYBOARD
  ========================================= */
  const onKeydown = (e) => {
    if (e.key === "Escape" && props.show) {
      handleClose();
    }
  };

  onMounted(() => {
    window.addEventListener("keydown", onKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", onKeydown);
    if (donutChartInstance) donutChartInstance.destroy();
    if (barChartInstance) barChartInstance.destroy();
  });

  const handleClose = () => {
    emit("close");
  };
</script>

<style scoped>
  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 0.2s ease;
  }
  .modal-fade-enter-from,
  .modal-fade-leave-to {
    opacity: 0;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(203, 213, 225, 0.5);
    border-radius: 9999px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.8);
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(38, 52, 73, 0.8);
  }
</style>
